import { useAuth, useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react'
import type { ClerkUser } from '@saaga/shared/interfaces/auth.interface'
import {
  clearAuth,
  setMemberships,
  setOrganization,
  setUser,
} from '@saaga/shared/store/slices/auth.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { useEffect } from 'react'

import { detectAuthMethod } from '@/utils/auth-method.util'

import { useAgentsApiConfig } from './use-agents-api-config.hook'
import { getTimestamp, transformMembership, transformOrganization } from './use-auth-sync.helper'

export const useAuthSync = (): void => {
  const dispatch = useAppDispatch()

  const { isLoaded: authLoaded, isSignedIn, sessionId } = useAuth()
  const { isLoaded: userLoaded, user } = useUser()
  const { organization } = useOrganization()
  const { userMemberships } = useOrganizationList({ userMemberships: { infinite: true } })

  useAgentsApiConfig()

  useEffect(() => {
    if (!authLoaded || !userLoaded) return
    if (!isSignedIn || !user || !sessionId) {
      dispatch(clearAuth())
      return
    }
    const clerkUser: ClerkUser = {
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress ?? '',
      firstName: user.firstName,
      lastName: user.lastName,
      imageUrl: user.imageUrl,
      username: user.username,
      authMethod: detectAuthMethod(user.externalAccounts),
      createdAt: getTimestamp(user.createdAt),
      updatedAt: getTimestamp(user.updatedAt),
    }
    dispatch(setUser({ user: clerkUser, sessionId }))
  }, [authLoaded, userLoaded, isSignedIn, user, sessionId, dispatch])

  useEffect(() => {
    if (!organization) {
      dispatch(setOrganization(null))
      return
    }
    dispatch(setOrganization(transformOrganization(organization)))
  }, [organization, dispatch])

  useEffect(() => {
    if (!userMemberships?.data) {
      dispatch(setMemberships([]))
      return
    }
    dispatch(setMemberships(userMemberships.data.map(transformMembership)))
  }, [userMemberships?.data, dispatch])
}
